import React, { useEffect, useState } from 'react';
import Link from '../components/LocalizedLink';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, MessageCircle, Smartphone, Shield, Headphones, CheckCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';
import { motion, AnimatePresence } from 'framer-motion';

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [packageName, setPackageName] = useState('');

  useEffect(() => {
    if (location.state?.showSuccess) {
      setShowSuccessMessage(true);
      setPackageName(location.state.packageName || '');
      navigate(location.pathname, { replace: true, state: {} });
      const timer = setTimeout(() => setShowSuccessMessage(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [location.state, navigate]);

  const steps = [
    { num: "1", title: t('home.steps.1_title'), desc: t('home.steps.1_desc') },
    { num: "2", title: t('home.steps.2_title'), desc: t('home.steps.2_desc') },
    { num: "3", title: t('home.steps.3_title'), desc: t('home.steps.3_desc') }
  ];

  const benefits = [
    { 
      icon: Smartphone, 
      title: t('home.benefits.1_title'), 
      desc: t('home.benefits.1_desc'),
    },
    { 
      icon: Shield, 
      title: t('home.benefits.2_title'), 
      desc: t('home.benefits.2_desc'),
    },
    { 
      icon: Headphones, 
      title: t('home.benefits.3_title'), 
      desc: t('home.benefits.3_desc'),
    }
  ];

  return (
    <div data-testid="home-page">
      <AnimatePresence>
        {showSuccessMessage && (
          <motion.div
            initial={{ opacity: 0, y: -100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4"
            data-testid="success-message"
          >
            <div className="bg-emerald-500 text-white p-5 rounded-2xl shadow-2xl flex items-start gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-1">{t('home.success_title')}</h3>
                <p className="text-sm text-white/90">
                  {packageName ? `${t('home.success_package_prefix')} ${packageName}. ` : ''}
                  {t('home.success_desc')}
                </p>
              </div>
              <button 
                onClick={() => setShowSuccessMessage(false)}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-[var(--color-primary)] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-white/5 rounded-full" />
          <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] bg-white/5 rounded-full" />
          <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-white/5 rounded-full" />
          <div className="absolute top-32 right-1/4 w-32 h-32 opacity-30">
            <div className="grid grid-cols-4 gap-3">
              {[...Array(16)].map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full bg-white" />
              ))}
            </div>
          </div>
          <div className="absolute bottom-20 left-1/4 w-40 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
          <div className="absolute top-40 left-20 w-px h-32 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-2xl">
            <FadeInUp>
              <span className="text-white/80 uppercase text-sm font-semibold tracking-widest mb-4 inline-block">{t('home.hero_tagline')}</span>
            </FadeInUp>
            
            <FadeInUp delay={0.1}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6 leading-tight tracking-tight">
                {t('home.hero_title_1')}<br />
                <span className="text-white/90">{t('home.hero_title_2')}</span>
              </h1>
            </FadeInUp>
            
            <FadeInUp delay={0.2}>
              <p className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
                {t('home.hero_desc_1')} 
                <strong className="text-white"> {t('home.hero_desc_2')}</strong>
              </p>
            </FadeInUp>
            
            <FadeInUp delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/tariffe">
                  <Button 
                    data-testid="hero-cta-btn"
                    className="bg-white text-[var(--color-primary)] hover:bg-white/90 text-lg px-8 py-6 h-auto rounded-full font-semibold shadow-lg"
                  >
                    {t('common.scopri_prezzi')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/393534992310" target="_blank" rel="noopener noreferrer">
                  <Button 
                    variant="outline"
                    data-testid="hero-whatsapp-btn"
                    className="text-lg px-8 py-6 h-auto rounded-full border-2 border-white text-white hover:bg-white/10 transition-colors"
                  >
                    <MessageCircle className="mr-2 w-5 h-5" />
                    {t('common.scrivici')}
                  </Button>
                </a>
              </div>
            </FadeInUp>
            
            <FadeInUp delay={0.4}>
              <p className="text-sm text-white/60 mt-6">
                {t('home.hero_note')}
              </p>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* Come Funziona */}
      <section className="py-20 md:py-28 bg-[var(--color-surface)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-[var(--color-primary)]/5 rounded-full blur-2xl" />
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-[var(--color-accent)]/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <FadeInUp className="text-center mb-12">
            <span className="label-tag mb-2 inline-block text-sm">{t('home.how_tag')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-text)] tracking-tight">
              {t('home.how_title')}
            </h2>
          </FadeInUp>

          <StaggerContainer className="grid-borders grid md:grid-cols-3" staggerDelay={0.15}>
            {steps.map((step) => (
              <StaggerItem 
                key={step.num}
                className="p-8 md:p-10 text-center"
              >
                <div data-testid={`step-${step.num}`}>
                  <div className="step-number mx-auto mb-5">{step.num}</div>
                  <h3 className="text-xl font-bold text-[var(--color-text)] mb-2">
                    {step.title}
                  </h3>
                  <p className="text-[var(--color-text-muted)]">
                    {step.desc}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>

          <FadeInUp delay={0.3} className="text-center mt-10">
            <Link to="/come-funziona">
              <Button variant="link" className="text-[var(--color-primary)] font-semibold text-base">
                {t('home.how_details')}
                <ArrowRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </FadeInUp>
        </div>
      </section>

      {/* Benefici */}
      <section className="py-20 md:py-28 bg-[var(--color-accent)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute top-1/3 left-1/4 w-2 h-2 bg-white/20 rounded-full" />
          <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-white/15 rounded-full" />
        </div>
        
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <FadeInUp className="text-center mb-12">
            <span className="text-white/80 uppercase text-sm font-semibold tracking-widest mb-2 inline-block">{t('home.why_tag')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
              {t('home.why_title')}
            </h2>
          </FadeInUp>

          <StaggerContainer className="grid md:grid-cols-3 gap-6" staggerDelay={0.12}>
            {benefits.map((benefit, index) => (
              <StaggerItem key={index}>
                <div 
                  className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-8 text-center h-full"
                  data-testid={`benefit-${index}`}
                >
                  <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center mb-5 mx-auto">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed">
                    {benefit.desc}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA Finale */}
      <section className="py-20 md:py-28 bg-[var(--color-primary)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-white/5 rounded-full" />
          <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white/20 rounded-full" />
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-white/15 rounded-full" />
        </div>
        
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center relative z-10">
          <FadeInUp>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
              {t('home.cta_title')}
            </h2>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <p className="text-lg text-white/80 mb-8">
              {t('home.cta_desc')}
            </p>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/tariffe">
                <Button 
                  data-testid="cta-preventivo-btn"
                  className="bg-white text-[var(--color-primary)] hover:bg-white/90 rounded-full px-8 py-6 h-auto text-lg font-semibold shadow-lg"
                >
                  {t('common.scopri_prezzi')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contatti">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 h-auto text-lg font-semibold"
                >
                  {t('common.contattaci')}
                </Button>
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
